<template>
  <div class="ss-wrap" ref="wrap">
    <input
        ref="main"
        class="ss-input"
        type="text"
        autocomplete="off"
        :placeholder="placeholder"
        :title="query"
        v-model="query"
        @focus="openList"
        @click="openList"
        @input="onType"
        @keydown.esc="close"
        @keydown.enter.prevent="pickFirst"
    />
    <!-- невидимый input-прокси: нативная required-валидация формы, пока опция не выбрана -->
    <input v-if="required" class="ss-required-proxy" required tabindex="-1" aria-hidden="true"
           :value="isEmpty(value) ? '' : '1'"
           @focus="onProxyFocus"/>
    <div class="ss-list" v-if="opened" :style="listStyle">
      <div v-if="filtered.length === 0" class="ss-empty">Ничего не найдено</div>
      <div class="ss-option" v-for="(option, idx) in filtered" :key="idx"
           :title="optionTitle(option)"
           @mousedown.prevent="pick(option)">
        {{ label(option) }}
      </div>
    </div>
  </div>
</template>

<script>
/*
 * Выпадающий список с поиском БЕЗ учёта регистра (по подписи и title-строке).
 * v-model: сам объект-опция, либо option[valueKey], если задан valueKey.
 * @change стреляет только при реальном выборе опции.
 */
export default {
  name: 'SearchSelect',
  props: {
    value: {default: ''},
    options: {type: Array, default: () => []},
    label: {type: Function, required: true},
    title: {type: Function, default: null},
    valueKey: {type: String, default: null},
    placeholder: {type: String, default: 'Начните вводить для поиска'},
    required: {type: Boolean, default: false},
  },
  data() {
    return {
      query: '',
      opened: false,
      searching: false, // пока пользователь печатает — список фильтруется по введённому
      listStyle: {},
    };
  },
  computed: {
    filtered() {
      if (!this.searching || this.query.trim() === '') return this.options;
      const q = this.query.trim().toLowerCase();
      return this.options.filter(o => this.searchText(o).indexOf(q) !== -1);
    },
  },
  watch: {
    value: {immediate: true, handler() { this.syncQuery(); }},
    options() { this.syncQuery(); },
  },
  mounted() {
    document.addEventListener('mousedown', this.onOutside);
    // capture — ловим прокрутку любого контейнера-предка (модалки и т.п.)
    window.addEventListener('scroll', this.placeList, true);
    window.addEventListener('resize', this.placeList);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onOutside);
    window.removeEventListener('scroll', this.placeList, true);
    window.removeEventListener('resize', this.placeList);
  },
  methods: {
    searchText(o) {
      let text = String(this.label(o));
      if (this.title) text += ' ' + String(this.title(o));
      return text.toLowerCase();
    },
    optionTitle(o) { return this.title ? this.title(o) : this.label(o); },
    isEmpty(v) { return v === '' || v === null || v === undefined; },
    resolve(value) {
      if (this.isEmpty(value)) return null;
      if (this.valueKey) return this.options.find(o => o[this.valueKey] === value) || null;
      return value;
    },
    syncQuery() {
      const option = this.resolve(this.value);
      this.query = option ? String(this.label(option)) : '';
      this.searching = false;
    },
    openList() {
      this.opened = true;
      this.placeList();
    },
    // список позиционируется fixed по координатам инпута — так его не обрезают
    // контейнеры с overflow (модалки) и не перекрывают соседние элементы
    placeList() {
      if (!this.opened || !this.$refs.wrap) return;
      const rect = this.$refs.wrap.getBoundingClientRect();
      const style = {left: rect.left + 'px', width: rect.width + 'px'};
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < 240 && rect.top > spaceBelow) {
        style.bottom = (window.innerHeight - rect.top) + 'px';
        style.maxHeight = Math.min(230, rect.top - 10) + 'px';
      } else {
        style.top = rect.bottom + 'px';
        style.maxHeight = Math.min(230, spaceBelow - 10) + 'px';
      }
      this.listStyle = style;
    },
    close() { this.opened = false; this.syncQuery(); },
    onOutside(e) { if (this.$refs.wrap && !this.$refs.wrap.contains(e.target)) this.close(); },
    onProxyFocus() {
      // браузер фокусирует прокси при провале валидации — переводим фокус на видимый инпут
      if (this.$refs.main) this.$refs.main.focus();
    },
    onType() {
      this.searching = true;
      this.openList();
      // начали печатать поверх выбранного — выбор сбрасывается (как пустой select)
      if (!this.isEmpty(this.value)) this.$emit('input', this.valueKey ? '' : '');
    },
    pick(option) {
      this.$emit('input', this.valueKey ? option[this.valueKey] : option);
      this.$emit('change');
      this.opened = false;
      this.searching = false;
      this.$nextTick(this.syncQuery);
    },
    pickFirst() { if (this.opened && this.filtered.length > 0) this.pick(this.filtered[0]); },
  },
};
</script>

<style scoped>
.ss-wrap {
  position: relative;
  width: 98%;
  margin: .5rem;
}

.ss-input {
  width: 100%;
  padding: .5rem 2rem .5rem 1rem;
  border: 1px solid #b9c4d4;
  border-radius: .75rem;
  background: white url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23536181' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='m20 20-3.5-3.5'/%3E%3C/svg%3E") no-repeat right .7rem center;
  color: #23468c;
  font-size: .9rem;
  outline: none;
  text-overflow: ellipsis;
}

.ss-input:focus {
  border-color: #23468c;
}

.ss-input::placeholder {
  color: #8b97a9;
}

/* не display:none — иначе браузер не сможет показать подсказку валидации */
.ss-required-proxy {
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 1px;
  height: 1px;
  opacity: 0;
  border: none;
  padding: 0;
  pointer-events: none;
}

.ss-list {
  position: fixed; /* координаты задаются инлайн из placeList() */
  z-index: 4000;
  max-height: 230px;
  overflow-y: auto;
  background: white;
  border: 1px solid #b9c4d4;
  border-radius: .5rem;
  box-shadow: 0 6px 18px rgba(35, 70, 140, .18);
}

.ss-option {
  padding: .45rem .9rem;
  font-size: .85rem;
  color: #23468c;
  cursor: pointer;
  border-bottom: 1px solid #eef1f6;
  text-align: left;
}

.ss-option:last-child {
  border-bottom: none;
}

.ss-option:hover {
  background: #eaf1fb;
}

.ss-empty {
  padding: .45rem .9rem;
  font-size: .85rem;
  color: #8b97a9;
}
</style>
